import type { UseCoreResult } from './types'
import type { Store } from 'vuex'
import type { Router } from 'vue-router'

export type { UseCoreResult, CoreStoreState } from './types'
export { getCoreStoreState } from './types'

type CreateCoreOptions = {
  store: Store<unknown>
  router: Router
  i18n: unknown
}

type VueI18nBridge = {
  global: {
    t: (...args: [string, ...unknown[]]) => string
    tc: (...args: [string, ...unknown[]]) => string
    tm: (...args: [string, ...unknown[]]) => unknown
  }
}

let core: UseCoreResult | null = null
let coreShell: UseCoreResult | null = null
let lastCreateCoreOptions: CreateCoreOptions | null = null
let allowCoreRecreate = false
let factoryApiCall: UseCoreResult['apiCall'] | null = null

function buildApiCall (store: Store<unknown>): UseCoreResult['apiCall'] {
  return (...args: unknown[]): Promise<unknown> => {
    const firstArg = args[0] as { url: string }
    if (firstArg.url.match(/^\/loyalty-card-service\//)) {
      return store.dispatch('api/callLoyalty', ...args)
    } else if (firstArg.url.match(/^\/hint-and-walkthrough\//)) {
      return store.dispatch('api/callHintAndWalkthrough', ...args)
    } else {
      return store.dispatch('api/call', ...args)
    }
  }
}

function buildI18nHelpers (i18n: unknown) {
  const _i18n = i18n as unknown as VueI18nBridge

  const t = (...args: [string, ...unknown[]]): string => {
    if (args.length > 1) {
      return _i18n.global.tc(...args)
    }
    return _i18n.global.t(...args)
  }

  const tc = (...args: [string, ...unknown[]]): string => {
    return _i18n.global.tc(...args)
  }

  const tm = (...args: [string, ...unknown[]]): unknown => {
    return _i18n.global.tm(...args)
  }

  return { t, tc, tm }
}

function getCoreShell (): UseCoreResult {
  if (!coreShell) {
    coreShell = {} as UseCoreResult
  }

  return coreShell
}

function applyCoreOptions (
  target: UseCoreResult,
  { store, router, i18n }: CreateCoreOptions,
  { refreshApiCall }: { refreshApiCall: boolean }
): UseCoreResult {
  const { t, tc, tm } = buildI18nHelpers(i18n)

  target.store = store
  target.router = router
  target.i18n = i18n
  target.t = t
  target.tc = tc
  target.tm = tm

  if (refreshApiCall) {
    factoryApiCall = buildApiCall(store)
    target.apiCall = factoryApiCall
  }

  return target
}

function isApiCallPatched (target: UseCoreResult): boolean {
  return factoryApiCall !== null
    && target.apiCall !== undefined
    && target.apiCall !== factoryApiCall
}

function getPatchedValue<T> (
  current: T | undefined,
  original: T | undefined
): T | undefined {
  if (current === undefined || original === undefined) {
    return undefined
  }

  return current !== original ? current : undefined
}

export function createCore (options: CreateCoreOptions): UseCoreResult {
  if (core && !allowCoreRecreate) {
    throw new Error('createCore has already been called')
  }

  allowCoreRecreate = false
  lastCreateCoreOptions = options

  const target = getCoreShell()
  applyCoreOptions(target, options, { refreshApiCall: true })
  core = target

  return target
}

export function resetCore (): void {
  core = null
  allowCoreRecreate = true
}

/** Re-initializes core from the last createCore() call. Used by the unit test setup. */
export function restoreCore (): void {
  if (!core && lastCreateCoreOptions) {
    const target = getCoreShell()
    const patchedApiCall = isApiCallPatched(target) ? target.apiCall : undefined
    const patchedStore = getPatchedValue(target.store, lastCreateCoreOptions.store)
    const patchedRouter = getPatchedValue(target.router, lastCreateCoreOptions.router)
    const patchedI18n = getPatchedValue(target.i18n, lastCreateCoreOptions.i18n)

    applyCoreOptions(target, lastCreateCoreOptions, {
      refreshApiCall: patchedApiCall === undefined,
    })

    if (patchedStore !== undefined) {
      target.store = patchedStore
    }

    if (patchedRouter !== undefined) {
      target.router = patchedRouter
    }

    if (patchedI18n !== undefined) {
      target.i18n = patchedI18n
    }

    if (patchedApiCall !== undefined) {
      target.apiCall = patchedApiCall
    }

    core = target
  }
}

export function useCore (): UseCoreResult {
  if (!core) {
    throw new Error('useCore called before createCore')
  }

  return core
}
